/**
 * <ma-product-form>
 * Wraps the PDP form. On option change updates price, image, SKU and ATC
 * button live (no page reload). On submit posts via fetch to /cart/add.js
 * and opens the cart drawer with the new item.
 *
 * All DOM updates use textContent / createElement / DOMParser. No innerHTML
 * is set with user-influenced data.
 */
class MaProductForm extends HTMLElement {
  connectedCallback() {
    try {
      this.product = JSON.parse(this.dataset.productJson || "{}");
    } catch (e) {
      console.error("ma-product-form: invalid product JSON", e);
      return;
    }
    this.form = this.querySelector("form");
    this.idInput = this.form?.querySelector('input[name="id"]');
    if (!this.form || !this.idInput) return;

    this.optionInputs = this.form.querySelectorAll('input[name^="options["]');
    this.optionInputs.forEach((input) => {
      input.addEventListener("change", () => this._onOptionChange());
    });

    // Visual state for option pills
    this.form.querySelectorAll(".ma-pdp__option").forEach((fieldset) => {
      const labels = fieldset.querySelectorAll(".ma-pdp__option-pill");
      labels.forEach((lbl) => {
        const radio = lbl.querySelector('input[type="radio"]');
        if (!radio) return;
        radio.addEventListener("change", () => {
          if (radio.checked) {
            labels.forEach((x) => x.classList.remove("active"));
            lbl.classList.add("active");
            const legend = fieldset.querySelector(".ma-pdp__option-label strong");
            if (legend) legend.textContent = radio.value;
          }
        });
      });
    });

    // Subscription radios update the line-total + main price
    this.querySelectorAll('input[name="selling_plan"]').forEach((input) => {
      input.addEventListener("change", () => {
        this.querySelectorAll(".ma-pdp__purchase-card").forEach((c) => c.classList.remove("active"));
        input.closest(".ma-pdp__purchase-card")?.classList.add("active");
        this._updateMainPrice();
        this._updateLineTotal();
      });
    });

    // Qty stepper updates line-total (input + change events both)
    const qtyInput = this.querySelector("[data-qty-input]");
    if (qtyInput) {
      qtyInput.addEventListener("input", () => this._updateLineTotal());
      qtyInput.addEventListener("change", () => this._updateLineTotal());
    }

    // Form submit → AJAX add to cart
    this.form.addEventListener("submit", (e) => this._onSubmit(e));
  }

  _currentSelectedOptions() {
    const opts = [];
    this.querySelectorAll(".ma-pdp__option").forEach((fs) => {
      const checked = fs.querySelector('input[type="radio"]:checked');
      if (checked) opts.push(checked.value);
    });
    return opts;
  }

  _findVariant(selectedValues) {
    if (!this.product.variants) return null;
    return this.product.variants.find((v) => {
      const optsArr = [v.option1, v.option2, v.option3].filter(Boolean);
      if (optsArr.length !== selectedValues.length) return false;
      return optsArr.every((o, i) => o === selectedValues[i]);
    });
  }

  _onOptionChange() {
    const selected = this._currentSelectedOptions();
    const variant = this._findVariant(selected);
    if (!variant) {
      this._setUnavailable();
      return;
    }
    this.idInput.value = variant.id;

    // Update URL (?variant=)
    const url = new URL(window.location.href);
    url.searchParams.set("variant", variant.id);
    window.history.replaceState({}, "", url);

    // Price
    const priceEl = this.querySelector("[data-pdp-price]");
    if (priceEl) priceEl.textContent = this._money(variant.price);

    // Compare-at
    const compareEl = this.querySelector("[data-pdp-compare]");
    if (compareEl) {
      if (variant.compare_at_price && variant.compare_at_price > variant.price) {
        compareEl.textContent = this._money(variant.compare_at_price);
        compareEl.style.display = "";
      } else {
        compareEl.style.display = "none";
      }
    }
    this._syncDcto(variant);

    // SKU
    const skuEl = this.querySelector("[data-pdp-sku]");
    if (skuEl) skuEl.textContent = variant.sku || "";

    // Image — find matching figure by image src and toggle active state
    if (variant.featured_image) {
      const newSrc = variant.featured_image.src;
      const figs = document.querySelectorAll(".ma-pdp__main-figure");
      const thumbs = document.querySelectorAll(".ma-pdp__thumb");
      figs.forEach((f) => f.classList.remove("active"));
      thumbs.forEach((t) => { t.classList.remove("active"); t.setAttribute("aria-selected", "false"); });
      let matched = false;
      figs.forEach((f, i) => {
        const img = f.querySelector("img");
        if (!matched && img && img.src.includes(this._stripCdnSize(newSrc))) {
          f.classList.add("active");
          if (thumbs[i]) {
            thumbs[i].classList.add("active");
            thumbs[i].setAttribute("aria-selected", "true");
          }
          matched = true;
        }
      });
      if (!matched && figs[0]) {
        figs[0].classList.add("active");
        if (thumbs[0]) thumbs[0].classList.add("active");
      }
    }

    // Stock state
    const stockEl = this.querySelector(".ma-pdp__stock");
    if (stockEl) {
      stockEl.classList.toggle("ma-pdp__stock--out", !variant.available);
      // Find the text node and update it (preserves the dot span)
      const textNode = Array.from(stockEl.childNodes).find((n) => n.nodeType === Node.TEXT_NODE);
      if (textNode) textNode.textContent = variant.available ? "Disponible" : "Agotado";
    }

    // ATC button
    const atc = this.querySelector(".ma-pdp__atc");
    if (atc) {
      atc.disabled = !variant.available;
      if (variant.available) {
        this._renderATC(atc, variant);
      } else {
        atc.replaceChildren(document.createTextNode("Agotado"));
      }
    }
  }

  _currentVariant() {
    const selected = this._currentSelectedOptions();
    if (selected.length === 0) {
      // Single-variant product: use the id from the hidden input
      const id = parseInt(this.idInput.value, 10);
      return (this.product.variants || []).find((v) => v.id === id) || this.product.variants?.[0];
    }
    return this._findVariant(selected);
  }

  _currentUnitPrice(variant) {
    const planRadio = this.querySelector('input[name="selling_plan"]:checked');
    let unitPrice = variant.price;
    if (planRadio && planRadio.value && this.product.selling_plan_groups?.length) {
      const planId = parseInt(planRadio.value, 10);
      for (const group of this.product.selling_plan_groups) {
        const plan = group.selling_plans?.find((p) => p.id === planId);
        if (plan && plan.price_adjustments?.length) {
          const adj = plan.price_adjustments[0];
          if (adj.value_type === "fixed_amount") {
            unitPrice = Math.max(0, variant.price - adj.value);
          } else if (adj.value_type === "percentage") {
            unitPrice = Math.max(0, variant.price * (1 - adj.value / 100));
          } else if (adj.value_type === "price") {
            unitPrice = adj.value;
          }
          break;
        }
      }
    }
    return unitPrice;
  }

  _updateMainPrice() {
    const variant = this._currentVariant();
    if (!variant) return;
    const unitPrice = this._currentUnitPrice(variant);
    const priceEl = this.querySelector("[data-pdp-price]");
    const compareEl = this.querySelector("[data-pdp-compare]");
    if (priceEl) priceEl.textContent = this._money(unitPrice);
    // If subscription is active, show original price as compare
    const planRadio = this.querySelector('input[name="selling_plan"]:checked');
    if (compareEl) {
      if (planRadio && planRadio.value && unitPrice < variant.price) {
        compareEl.textContent = this._money(variant.price);
        compareEl.style.display = "";
      } else if (variant.compare_at_price && variant.compare_at_price > variant.price) {
        compareEl.textContent = this._money(variant.compare_at_price);
        compareEl.style.display = "";
      } else {
        compareEl.style.display = "none";
      }
    }
    this._syncDcto(variant);
  }

  // Pastilla «−X%» del precio: sólo descuento real (compare_at > price), no el
  // 10 % de suscripción, que ya tiene su propia etiqueta en las opciones.
  _syncDcto(variant) {
    // La pastilla vive sobre la foto, fuera del formulario: se busca en la ficha.
    const el = (this.closest(".ma-pdp") || document).querySelector("[data-pdp-dcto]");
    if (!el || !variant) return;
    const antes = variant.compare_at_price, ahora = variant.price;
    if (antes && antes > ahora) {
      el.textContent = "−" + Math.round(((antes - ahora) * 100) / antes) + "%";
      el.hidden = false;
    } else {
      el.hidden = true;
    }
  }

  _renderATC(atc, variant) {
    const qty = parseInt(this.querySelector("[data-qty-input]")?.value || "1", 10);
    const unitPrice = this._currentUnitPrice(variant);
    atc.replaceChildren();
    atc.appendChild(document.createTextNode("Agregar al carrito · "));
    const span = document.createElement("span");
    span.dataset.pdpLineTotal = "";
    span.textContent = this._money(unitPrice * qty);
    atc.appendChild(span);
  }

  _updateLineTotal() {
    const atc = this.querySelector(".ma-pdp__atc");
    if (!atc || atc.disabled) return;
    const variant = this._currentVariant();
    if (!variant || !variant.available) return;
    this._renderATC(atc, variant);
  }

  _setUnavailable() {
    const atc = this.querySelector(".ma-pdp__atc");
    if (atc) {
      atc.disabled = true;
      atc.replaceChildren(document.createTextNode("Combinación no disponible"));
    }
  }

  async _onSubmit(e) {
    e.preventDefault();
    const atc = this.querySelector(".ma-pdp__atc");
    const originalChildren = atc ? Array.from(atc.childNodes) : [];
    if (atc) {
      atc.disabled = true;
      atc.replaceChildren(document.createTextNode("Agregando…"));
    }
    try {
      const formData = new FormData(this.form);
      formData.append("sections", "cart-drawer");
      formData.append("sections_url", window.location.pathname);
      const res = await fetch("/cart/add.js", {
        method: "POST",
        body: formData,
        headers: { "Accept": "application/json" },
      });
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        throw new Error(err.description || err.message || "No se pudo agregar al carrito");
      }
      const result = await res.json();
      if (result.sections && result.sections["cart-drawer"]) {
        renderDrawerSection(result.sections["cart-drawer"]);
      } else if (window.MaCart?.refresh) {
        await window.MaCart.refresh();
      }
      if (window.MaCart?.agregado) window.MaCart.agregado(); else if (window.MaCart?.open) window.MaCart.open();
      pulseCartCount();
    } catch (err) {
      console.error("Cart add failed", err);
      alert(err.message || "No se pudo agregar al carrito");
    } finally {
      if (atc) {
        atc.disabled = false;
        if (originalChildren.length) atc.replaceChildren(...originalChildren);
      }
    }
  }

  _money(cents) {
    const currency = this.product.currency || (window.Shopify?.currency?.active) || "MXN";
    const value = cents / 100;
    // If integer, no decimals. Else show 2.
    const isInt = Math.abs(value - Math.round(value)) < 0.005;
    return value.toLocaleString("es-MX", {
      style: "currency",
      currency,
      minimumFractionDigits: isInt ? 0 : 2,
      maximumFractionDigits: isInt ? 0 : 2,
    });
  }

  _stripCdnSize(src) {
    return src.replace(/_\d+x\d*\./, ".").replace(/_\d+x\./, ".");
  }
}

customElements.define("ma-product-form", MaProductForm);

// ────────────────────────────────────────────────────────────────────
// Shared helpers (used by quick-add too)
// ────────────────────────────────────────────────────────────────────
function renderDrawerSection(html) {
  const drawer = document.querySelector("ma-cart-drawer");
  if (!drawer) return;
  // Use DOMParser to safely parse server-rendered HTML and pluck nodes.
  const doc = new DOMParser().parseFromString(html, "text/html");
  const newDrawer = doc.querySelector("ma-cart-drawer");
  if (!newDrawer) return;
  drawer.replaceChildren(...Array.from(newDrawer.childNodes));
  drawer.querySelectorAll("[data-ma-drawer-close]").forEach((el) => {
    el.addEventListener("click", () => drawer.close());
  });
}

function pulseCartCount() {
  document.querySelectorAll("[data-ma-cart-count]").forEach((el) => {
    el.style.transition = "transform 200ms cubic-bezier(.22,.61,.36,1)";
    el.style.transform = "scale(1.4)";
    setTimeout(() => { el.style.transform = ""; }, 220);
  });
}

// ────────────────────────────────────────────────────────────────────
// Quick-add from product cards (.ma-card__quick-add)
// ────────────────────────────────────────────────────────────────────
document.addEventListener("click", async (e) => {
  const btn = e.target.closest(".ma-card__quick-add");
  if (!btn) return;
  e.preventDefault();
  e.stopPropagation();

  const variantId = btn.dataset.variantId;
  if (!variantId) return;

  const originalChildren = Array.from(btn.childNodes);
  btn.disabled = true;
  btn.style.opacity = "1";
  btn.replaceChildren(document.createTextNode("…"));

  try {
    const formData = new FormData();
    formData.append("id", variantId);
    formData.append("quantity", "1");
    formData.append("sections", "cart-drawer");
    formData.append("sections_url", window.location.pathname);
    const res = await fetch("/cart/add.js", {
      method: "POST",
      body: formData,
      headers: { "Accept": "application/json" },
    });
    if (!res.ok) {
      const err = await res.json().catch(() => ({}));
      throw new Error(err.description || err.message || "Error agregando");
    }
    const result = await res.json();
    if (result.sections && result.sections["cart-drawer"]) {
      renderDrawerSection(result.sections["cart-drawer"]);
    } else if (window.MaCart?.refresh) {
      await window.MaCart.refresh();
    }
    if (window.MaCart?.agregado) window.MaCart.agregado(); else if (window.MaCart?.open) window.MaCart.open();

    btn.replaceChildren(document.createTextNode("✓"));
    setTimeout(() => { btn.replaceChildren(...originalChildren); btn.disabled = false; }, 900);
  } catch (err) {
    console.error("Quick-add failed", err);
    btn.replaceChildren(document.createTextNode("✕"));
    setTimeout(() => { btn.replaceChildren(...originalChildren); btn.disabled = false; }, 1500);
  }
});

// Card-grid handlers moved to assets/component-card-grid.js (loaded globally
// from theme.liquid since cards appear in many sections, not just PDP).
